import { AuthenticationResult } from "@azure/msal-browser";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TokenClaims } from "@azure/msal-common";

type AuthenticationStatus = "authenticated" | "unauthenticated" | "loading" | "error";

export type IUser = {
  aud?: string;
  email: string;
  iss?: string;
  iat?: number;
  exp?: number;
  nbf?: number;
  oid?: string;
  sid?: string;
  tid?: string;
  accessToken: string;
  roles?: string[];
  name?: string;
};

interface IAuthState {
  status: AuthenticationStatus;
  user: IUser | null;
  graphToken?: string;
  powerbiToken?: string;
  oneClickCxReportToken?: string;
  error: string | null;
}

const initialState: IAuthState = {
  status: "unauthenticated",
  user: null,
  graphToken: undefined,
  powerbiToken: undefined,
  oneClickCxReportToken: undefined,
  error: null
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateAuthResult(state, { payload }: PayloadAction<AuthenticationResult>) {
      const { aud, iss, iat, exp, nbf, oid, sid, tid, roles, name } = payload?.idTokenClaims as TokenClaims;
      state.status = "authenticated";
      state.user = {
        email: payload.account?.username || "",
        accessToken: payload.accessToken,
        aud,
        iss,
        iat,
        exp,
        nbf,
        oid,
        sid,
        tid,
        roles,
        name
      } as IUser;
    },
    updateGraphToken(state, { payload }: PayloadAction<string>) {
      state.graphToken = payload;
    },
    updatePbiToken(state, { payload }: PayloadAction<string>) {
      state.powerbiToken = payload;
    },
    updateOneClickCxReportToken(state, { payload }: PayloadAction<string>) {
      state.oneClickCxReportToken = payload;
    }
  }
});

export const { updateAuthResult, updateGraphToken, updatePbiToken, updateOneClickCxReportToken } = authSlice.actions;
export default authSlice.reducer;
export const selectAuthUser = (state: { auth: IAuthState }) => state.auth.user;
export const selectGraphToken = (state: { auth: IAuthState }) => state.auth.graphToken;
export const selectOneClickCxReportToken = (state: { auth: IAuthState }) => state.auth.oneClickCxReportToken;
export const selectPbiToken = (state: { auth: IAuthState }) => state.auth.powerbiToken;
export const selectUserToken = (state: { auth: IAuthState }) => state.auth.user?.accessToken;
